// eslint-disable-next-line no-use-before-define
import React, {
  lazy,
  ReactElement,
  Suspense,
  useCallback,
  useEffect,
} from 'react';
import Axios from 'axios';
import { BrowserRouter, Route, Switch, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { createZitadelAuth } from 'pages/sso/zitadle';
import hasSsoToken from 'pages/sso/service';

import { config, apiBaseUrl } from 'core/config';
import { ApplicationState } from 'store/combineReducers';
import { Layout } from 'layout';
import { PrivateRoute } from 'shared/components/privateRouter';
import { Loader } from 'shared/components';
import { RolesEnum } from 'core/models';
import { getStoredSsoConfig } from 'utils/localStorage';
import { lazyWithPreload } from 'utils/preloadRoute';
import { getTenantId, isMobileApp } from 'utils/util';
import { message, notification } from 'antd';
import { NotificationCenterController } from 'baseComponent/notificationCenter';
import getSetting from 'core/service/setting.service';
import { parseAccessToken } from 'utils/jtw';
import { getCurrentUser, initSite } from './routes.service';

const routes: any[] = [];
const loginInRoutes: any[] = [];
const SiteEntry = lazyWithPreload('SiteEntry', () => import('pages/siteEntry'));
routes.push(SiteEntry);
const Home = lazyWithPreload('Home', () => import('pages/home'));
routes.push(Home);

const SupersetContainer = lazyWithPreload(
  'SsoLanding',
  () => import('pages/supersetContainer'),
);
routes.push(SupersetContainer);

const SupersetCalendar = lazyWithPreload(
  'SsoLanding',
  () => import('pages/supersetCalendar'),
);
routes.push(SupersetCalendar);

const NotFound = lazyWithPreload('NotFound', () => import('pages/notFound'));
routes.push(NotFound);

const Profile = lazyWithPreload('Profile', () => import('pages/profile'));
routes.push(Profile);

const APIKey = lazyWithPreload('APIKey', () => import('pages/profile/apikey'));
const AgentSettings = lazyWithPreload(
  'AgentSettings',
  () => import('pages/profile/agentSettings'),
);
const OAuthConsent = lazyWithPreload(
  'OAuthConsent',
  () => import('pages/oauth/consent'),
);
const McpSetting = lazyWithPreload(
  'McpSetting',
  () => import('pages/settings/mcpSetting'),
);
routes.push(APIKey);

const TotpConfirm = lazyWithPreload(
  'TotpConfirm',
  () => import('pages/signIn/LoginWithTOTP'),
);
loginInRoutes.push(TotpConfirm);

const Authentication = lazyWithPreload(
  'Authentication',
  () => import('pages/authenticationSetting/authentication'),
);
loginInRoutes.push(Authentication);

const SsoLanding = lazyWithPreload(
  'SsoLanding',
  () => import('pages/sso/landing'),
);
loginInRoutes.push(SsoLanding);

const SsoCallback = lazyWithPreload(
  'SsoCallback',
  () => import('pages/sso/callback'),
);
loginInRoutes.push(SsoCallback);

const RestorePassword = lazyWithPreload(
  'RestorePassword',
  () => import('pages/restorePassword'),
);
loginInRoutes.push(RestorePassword);

const RechangePassword = lazyWithPreload(
  'RechangePassword',
  () => import('pages/rechangePassword'),
);
loginInRoutes.push(RechangePassword);

const ResetPassword = lazyWithPreload(
  'ResetPassword',
  () => import('pages/resetPassword'),
);
loginInRoutes.push(ResetPassword);

const ClinicsManagement = lazyWithPreload(
  'ClinicsManagement',
  () => import('pages/tenantManagement'),
);
routes.push(ClinicsManagement);

const ClinicAdd = lazyWithPreload(
  'ClinicAdd',
  () => import('pages/tenantManagement/pages/clinicAdd'),
);
routes.push(ClinicAdd);

const ClinicEdit = lazyWithPreload(
  'ClinicEdit',
  () => import('pages/tenantManagement/pages/clinicEdit'),
);
routes.push(ClinicEdit);

const UsersManagement = lazyWithPreload(
  'UsersManagement',
  () => import('pages/usersManagement/IndexManagement'),
);
routes.push(UsersManagement);

const UserAdd = lazyWithPreload(
  'UserAdd',
  () => import('pages/usersManagement/pages/userAdd'),
);
routes.push(UserAdd);

const UserEdit = lazyWithPreload(
  'UserEdit',
  () => import('pages/usersManagement/pages/userEdit'),
);
routes.push(UserEdit);

const ProcessManagement = lazyWithPreload(
  'ProcessManagement',
  () => import('pages/settings/process'),
);
routes.push(ProcessManagement);

const DevicesManagement = lazyWithPreload(
  'DevicesManagement',
  () => import('pages/devicesManagement'),
);
routes.push(DevicesManagement);

const SensorsManagement = lazyWithPreload(
  'SensorsManagement',
  () => import('pages/sensorsManagement'),
);
routes.push(SensorsManagement);

const PatientManagement = lazyWithPreload(
  'PatientManagement',
  () => import('pages/patientsManagement'),
);
routes.push(PatientManagement);

const PatientsAdd = lazyWithPreload(
  'PatientsAdd',
  () => import('pages/patientsManagement/pages/patientAdd'),
);
routes.push(PatientsAdd);

const PatientsEdit = lazyWithPreload(
  'PatientsEdit',
  () => import('pages/patientsManagement/pages/patientEdit'),
);
routes.push(PatientsEdit);

const PatientsTabs = lazyWithPreload(
  'PatientsTabs',
  () => import('pages/patientsManagement/components/patientTabs'),
);
routes.push(PatientsTabs);

const PatientSensor = lazyWithPreload(
  'PatientSensor',
  () => import('pages/patientsManagement/pages/patientSensor'),
);
routes.push(PatientSensor);

const PatientSensorAdd = lazyWithPreload(
  'PatientSensorAdd',
  () => import('pages/patientsManagement/pages/patientSensorAdd'),
);
routes.push(PatientSensorAdd);

const PatientDashboard = lazyWithPreload(
  'PatientDashboard',
  () => import('pages/patientsManagement/pages/patientDashboard'),
);
routes.push(PatientDashboard);
const DisclosureView = lazyWithPreload(
  'DisclosureView',
  () => import('pages/patientsManagement/pages/disclosureView'),
);
routes.push(DisclosureView);

const MultiVitalsView = lazyWithPreload(
  'MultiVitalsView',
  () => import('pages/patientsManagement/pages/multi-vital'),
);
routes.push(MultiVitalsView);

const MonitoringProtocolsManagement = lazyWithPreload(
  'MonitoringProtocolsManagement',
  () => import('pages/monitoringProtocolsManagement'),
);
routes.push(MonitoringProtocolsManagement);

const MonitoringProtocolsAdd = lazyWithPreload(
  'MonitoringProtocolsAdd',
  () =>
    import('pages/monitoringProtocolsManagement/pages/monitoringProtocolAdd'),
);
routes.push(MonitoringProtocolsAdd);

const MonitoringProtocolsEdit = lazyWithPreload(
  'MonitoringProtocolsEdit',
  () =>
    import('pages/monitoringProtocolsManagement/pages/monitoringProtocolEdit'),
);
routes.push(MonitoringProtocolsEdit);

const DashboardManagementEvent = lazyWithPreload(
  'DashboardManagementEvent',
  () => import('pages/dashboardManagement/event'),
);
routes.push(DashboardManagementEvent);

const DashboardManagementRpmAlert = lazyWithPreload(
  'DashboardManagementRpmAlert',
  () => import('pages/dashboardManagement/rpmAlert'),
);
routes.push(DashboardManagementRpmAlert);

const EcgDataChunks = lazyWithPreload(
  'EcgDataChunks',
  () => import('pages/ecgDataChunks'),
);
routes.push(EcgDataChunks);

const ReportsManagement = lazyWithPreload(
  'ReportsManagement',
  () => import('pages/reports'),
);
routes.push(ReportsManagement);

const ArchiveManagement = lazyWithPreload(
  'ArchiveManagement',
  () => import('pages/dataExport'),
);
routes.push(ArchiveManagement);

const GenerateReport = lazyWithPreload(
  'GenerateReport',
  () => import('pages/generateReport'),
);
routes.push(GenerateReport);

const ReportsPdf = lazyWithPreload(
  'ReportsPdf',
  () => import('pages/reportsPdf'),
);
routes.push(ReportsPdf);

const AnalysisSetting = lazyWithPreload(
  'AnalysisSetting',
  () => import('pages/settings/analysisSetting'),
);
routes.push(AnalysisSetting);

const OtherSetting = lazyWithPreload(
  'OtherSetting',
  () => import('pages/settings/otherSetting'),
);
routes.push(OtherSetting);

const SSOSetting = lazyWithPreload(
  'SSOSetting',
  () => import('pages/settings/ssoSetting'),
);
routes.push(SSOSetting);

const PatientTemplate = lazyWithPreload(
  'PatientTemplate',
  () => import('pages/settings/patientTemplate'),
);
routes.push(PatientTemplate);

const GroupManagement = lazyWithPreload(
  'GroupManagement',
  () => import('pages/settings/groupManagement'),
);
routes.push(GroupManagement);

const History = lazyWithPreload('History', () => import('pages/history'));
routes.push(History);

const ECGRecordDashboard = lazyWithPreload(
  'ECGRecordDashboard',
  () => import('pages/patientsManagement/pages/ecgRecordDashboard'),
);
routes.push(ECGRecordDashboard);

const Sites = lazyWithPreload('Sites', () => import('pages/sites'));
routes.push(Sites);

const Studies = lazyWithPreload('Studies', () => import('pages/studies'));
routes.push(Studies);

const StudiesView = lazyWithPreload(
  'StudiesView',
  () => import('pages/studies/studiesView'),
);
routes.push(StudiesView);

const StudiesViewPatients = lazyWithPreload(
  'StudiesViewPatients',
  () => import('pages/studies/studiesViewPatients'),
);
routes.push(StudiesViewPatients);

const StudiesViewSites = lazyWithPreload(
  'StudiesViewSites',
  () => import('pages/studies/studiesViewSites'),
);
routes.push(StudiesViewSites);

const TenantDevice = lazyWithPreload(
  'TenantDevice',
  () => import('pages/tenant/device'),
);
routes.push(TenantDevice);

const TenantDeviceDetails = lazyWithPreload(
  'TenantDeviceDetails',
  () => import('pages/tenant/device/page/details'),
);
routes.push(TenantDeviceDetails);

const Download = lazyWithPreload('Download', () => import('pages/download'));
routes.push(Download);

const EmailTemplate = lazyWithPreload(
  'EmailTemplate',
  () => import('pages/settings/email'),
);
routes.push(EmailTemplate);

const EmailTemplateAdd = lazyWithPreload(
  'EmailTemplateAdd',
  () => import('pages/settings/email/page/add'),
);
routes.push(EmailTemplateAdd);

const EmailTemplateEdit = lazyWithPreload(
  'EmailTemplateEdit',
  () => import('pages/settings/email/page/edit'),
);
routes.push(EmailTemplateEdit);

const SmsTemplate = lazyWithPreload(
  'SmsTemplate',
  () => import('pages/settings/sms'),
);
routes.push(SmsTemplate);

const SmsTemplateAdd = lazyWithPreload(
  'SmsTemplateAdd',
  () => import('pages/settings/sms/page/add'),
);
routes.push(SmsTemplateAdd);

const SmsTemplateEdit = lazyWithPreload(
  'SmsTemplateEdit',
  () => import('pages/settings/sms/page/edit'),
);
routes.push(SmsTemplateEdit);

const RpmRT = lazyWithPreload('RpmRT', () => import('pages/rpm/dashboard'));
routes.push(RpmRT);

const RpmRTList = lazyWithPreload(
  'RpmRTList',
  () => import('pages/rpm/dashboard/ListView'),
);
routes.push(RpmRTList);

const AddRpmPatient = lazyWithPreload(
  'AddRpmPatient',
  () => import('pages/rpm/addPatient'),
);
routes.push(AddRpmPatient);

const DataAnalyticsDashboard = lazyWithPreload(
  'DataAnalyticsDashboard',
  () => import('pages/dataAnalytics/dashboard'),
);
routes.push(DataAnalyticsDashboard);

const AssignedUsers = lazyWithPreload(
  'AssignedUsers',
  () => import('pages/dataAnalytics/assignedUsers'),
);
routes.push(AssignedUsers);

const DataAnalyticsDashboardDetails = lazyWithPreload(
  'DataAnalyticsDashboardDetails',
  () => import('pages/dataAnalytics/chart'),
);
routes.push(DataAnalyticsDashboardDetails);

const Downloads = lazyWithPreload('Downloads', () => import('pages/downloads'));
routes.push(Downloads);

const {
  baseUrl,
  signIn,
  download,
  forbidden,
  clinics,
  profile,
  apikey,
  agentSettings,
  oauthConsent,
  mcpSetting,
  authentication,
  totpConfirm,
  // changePassword,
  restorePassword,
  rechangePassword,
  verifyPassword,
  addPath,
  // pmManagement,
  // adminManagement,
  // techManagement,
  // physicianManagement,
  devices,
  sensors,
  patients,
  monitoringProtocols,
  dashboard,
  dashboardEvent,
  dashboardRpmAlert,
  ecgDataChunks,
  pec,
  process,
  reports,
  reportArchive,
  generateReport,
  reportsPdf,
  reportsAbpmPdf,
  settings,
  analysisSetting,
  otherSetting,
  ssoSetting,
  patientTemplate,
  groupManagement,
  historyView,
  ecgRecord,
  sites,
  studies,
  // researchersManagement,
  // clinicalTrialCoordinatorManagement,
  userManagement,
  // superAdminManagement,
  emailTemplate,
  smsTemplate,
  dataAnalytics,
  assignedUsers,
  dataAnalyticsDetails,
  downloads,

  rpmRT,
  rpmRTList,
  addRpmPatient,

  supersetContainer,
  supersetCalendar,

  tenant,
  tenantDevice,
} = config.paths;

NotificationCenterController.replaceAntNotification(
  notification,
).replaceAntMessage(message);

export default function Routes(): ReactElement {
  const { isAuth, user } = useSelector(
    (state: ApplicationState) => state.session,
  );

  const getCurrentUserCallback = useCallback(async () => {
    try {
      await getCurrentUser();
      await getSetting();
      initSite(getTenantId());
    } catch (e) {
      // console.error('get current user callback error: ', e);
    }
  }, []);

  useEffect(() => {
    if (window.location.pathname !== supersetCalendar && !isMobileApp()) {
      if ('requestIdleCallback' in window) {
        let i = 0;
        requestIdleCallback(() => {
          for (; i < routes.length; ) {
            routes[i].preload();
            i += 1;
          }
          console.log(`================preload${i}=============`);
        });
      } else {
        // 兼容方案
        setTimeout(() => {
          routes.forEach((item) => item.preload());
        }, 3000);
      }
    }
    loginInRoutes.forEach((item) => item.preload());
  }, []);

  useEffect(() => {
    if (!user) {
      if (hasSsoToken()) {
        const ssoConfig = getStoredSsoConfig();
        const sso = createZitadelAuth(ssoConfig);
        sso.userManager.getUser().then((authUser) => {
          if (authUser) {
            getCurrentUserCallback();
          }
        });
      }
    }
  }, [user, getCurrentUserCallback]);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    const token = parseAccessToken() || {};
    const passMFA = !(token.mfa_required && !token.mfa_verified);
    if (isAuth && !user && passMFA) {
      getCurrentUserCallback();
    }
    return (): void => {
      source.cancel();
    };
  }, [isAuth, user, getCurrentUserCallback]);

  return (
    <Loader>
      <BrowserRouter>
        <Suspense fallback={null}>
          <Switch>
            <Route path="/agent-frontend/" exact>
              <Helmet>
                <title>404</title>
                <meta name="description" content="ERR_CONNECTION_RESET" />
              </Helmet>
              <NotFound
                code={404}
                from="agent"
                message="ERR_CONNECTION_RESET"
              />
            </Route>
            <Route path="/error" exact>
              <Helmet>
                <title>Error</title>
                <meta name="description" content="ERR_CONNECTION" />
              </Helmet>
              <NotFound from="api" />
            </Route>
            <Route path={`${reportsPdf}/:id`} exact>
              <Helmet>
                <title>Reports Pdf</title>
                <meta name="description" content="Reports Pdf" />
              </Helmet>
              <ReportsPdf />
            </Route>
            {/* <Route path={`${reportsAbpmPdf}/:id`} exact>
              <Helmet>
                <title>Reports Abpm Pdf</title>
                <meta name="description" content="Reports Abpm Pdf" />
              </Helmet>
              <ReportsAbpm />
            </Route> */}
            <Route path={supersetContainer} exact>
              <Helmet>
                <title>Superset Calendar</title>
                <meta name="description" content="Superset Calendar" />
              </Helmet>
              <SupersetContainer />
            </Route>
            <Route path={supersetCalendar} exact>
              <SupersetCalendar />
            </Route>
            <Route path={totpConfirm} exact>
              <TotpConfirm />
            </Route>
            <PrivateRoute
              path={`/history${patients}/:id`}
              isAuthenticated={isAuth}
              exact
              title="Multi-Vitals"
              metaContent="Multi-Vitals"
            >
              <MultiVitalsView />
            </PrivateRoute>
            <Route path={baseUrl} exact>
              <SiteEntry />
            </Route>
            <Route path={`${verifyPassword}/:token`} exact>
              <Helmet>
                <title>Verify Password</title>
                <meta name="description" content="Verify Password" />
              </Helmet>
              <ResetPassword />
            </Route>
            <Route path={`${restorePassword}/:token`} exact>
              <Helmet>
                <title>Reset Password</title>
                <meta name="description" content="Reset Password" />
              </Helmet>
              <ResetPassword />
            </Route>
            <Route path={`${rechangePassword}`} exact>
              <Helmet>
                <title>Reset Password</title>
                <meta name="description" content="Reset Password" />
              </Helmet>
              <RechangePassword />
            </Route>
            <Layout>
              <Switch>
                <Route path="/callback" exact>
                  <SsoCallback />
                </Route>
                <Route path={signIn} exact>
                  <SsoLanding />
                </Route>
                <PrivateRoute
                  path={`${reportsPdf}/edit/:id`}
                  isAuthenticated={isAuth}
                  exact
                  title="Reports Pdf"
                  metaContent="Reports Pdf"
                >
                  <ReportsPdf />
                </PrivateRoute>
                {user?.tenant?.downloadEnabled && (
                  <PrivateRoute
                    path={downloads}
                    isAuthenticated={isAuth}
                    exact
                    title="Downloads"
                    metaContent="Downloads"
                  >
                    <Downloads />
                  </PrivateRoute>
                )}

                <PrivateRoute
                  path={`${rpmRT}`}
                  isAuthenticated={isAuth}
                  exact
                  title="Dashboard RPM"
                  metaContent="Dashboard RPM"
                >
                  <RpmRT />
                </PrivateRoute>
                <PrivateRoute
                  path={`${rpmRTList}`}
                  isAuthenticated={isAuth}
                  exact
                  title="Dashboard RPM"
                  metaContent="Dashboard RPM"
                >
                  <RpmRTList />
                </PrivateRoute>
                <PrivateRoute
                  path={`${addRpmPatient}`}
                  isAuthenticated={isAuth}
                  exact
                  title="Add RPM Patient"
                  metaContent="Add RPM Patient"
                >
                  <AddRpmPatient />
                </PrivateRoute>
                <PrivateRoute
                  path={`${dataAnalytics}`}
                  isAuthenticated={isAuth}
                  exact
                  title="Data Analytics"
                  metaContent="Data Analytics"
                >
                  <DataAnalyticsDashboard />
                </PrivateRoute>
                <PrivateRoute
                  path={`${dataAnalytics}/:id${dataAnalyticsDetails}`}
                  isAuthenticated={isAuth}
                  exact
                  title="Data Analytics Details"
                  metaContent="Data Analytics Details"
                >
                  <DataAnalyticsDashboardDetails />
                </PrivateRoute>
                <PrivateRoute
                  path={`${dataAnalytics}/:id${assignedUsers}`}
                  isAuthenticated={isAuth}
                  exact
                  title="Assigned Users"
                  metaContent="Assigned Users"
                >
                  <AssignedUsers />
                </PrivateRoute>
                <PrivateRoute
                  path={`${emailTemplate}`}
                  isAuthenticated={isAuth}
                  exact
                  title="Email Template"
                  metaContent="Email Template"
                >
                  <EmailTemplate />
                </PrivateRoute>
                <PrivateRoute
                  path={`${emailTemplate}${addPath}`}
                  isAuthenticated={isAuth}
                  exact
                  title="Email Template Add"
                  metaContent="Email Template Add"
                >
                  <EmailTemplateAdd />
                </PrivateRoute>
                <PrivateRoute
                  path={`${emailTemplate}/:id`}
                  isAuthenticated={isAuth}
                  exact
                  title="Email Template Edit"
                  metaContent="Email Template Edit"
                >
                  <EmailTemplateEdit />
                </PrivateRoute>
                <PrivateRoute
                  path={`${smsTemplate}`}
                  isAuthenticated={isAuth}
                  exact
                  title="SMS Template"
                  metaContent="SMS Template"
                >
                  <SmsTemplate />
                </PrivateRoute>
                <PrivateRoute
                  path={`${smsTemplate}${addPath}`}
                  isAuthenticated={isAuth}
                  exact
                  title="SMS Template Add"
                  metaContent="SMS Template Add"
                >
                  <SmsTemplateAdd />
                </PrivateRoute>
                <PrivateRoute
                  path={`${smsTemplate}/:id`}
                  isAuthenticated={isAuth}
                  exact
                  title="SMS Template Edit"
                  metaContent="SMS Template Edit"
                >
                  <SmsTemplateEdit />
                </PrivateRoute>
                <PrivateRoute
                  path={`${tenantDevice}`}
                  isAuthenticated={isAuth}
                  exact
                  title="Device Management"
                  metaContent="Device Management"
                >
                  <TenantDevice />
                </PrivateRoute>
                <PrivateRoute
                  path={`${tenantDevice}/:id/:type/:tenantName`}
                  isAuthenticated={isAuth}
                  exact
                  title="Device Management"
                  metaContent="Device Management"
                >
                  <TenantDeviceDetails />
                </PrivateRoute>
                <PrivateRoute
                  path={`${studies}/:id${patients}`}
                  isAuthenticated={isAuth}
                  exact
                  title="studies patients"
                  metaContent="studies patients"
                >
                  <PatientManagement />
                </PrivateRoute>
                <PrivateRoute
                  path={`${studies}`}
                  isAuthenticated={isAuth}
                  exact
                  title="Studies"
                  metaContent="Studies"
                >
                  <Studies />
                </PrivateRoute>
                {/* <PrivateRoute
                  path={`${studies}/:id`}
                  isAuthenticated={isAuth}
                  exact
                  title="Patients Dashboard Management"
                  metaContent="Patients Dashboard Management"
                >
                  <StudiesView />
                </PrivateRoute> */}
                <PrivateRoute
                  path={`${studies}/:id/:studyId`}
                  isAuthenticated={isAuth}
                  exact
                  title="Patients Dashboard Management"
                  metaContent="Patients Dashboard Management"
                >
                  <StudiesView />
                </PrivateRoute>
                <PrivateRoute
                  path={`${studies}/:id${patients}/:studyId`}
                  isAuthenticated={isAuth}
                  exact
                  title="Patients Dashboard Management"
                  metaContent="Patients Dashboard Management"
                >
                  <StudiesViewPatients />
                </PrivateRoute>
                <PrivateRoute
                  path={`${studies}-sites/:id/`}
                  isAuthenticated={isAuth}
                  exact
                  title="studies Sites"
                  metaContent="studies Sites"
                >
                  <StudiesViewSites />
                </PrivateRoute>
                <PrivateRoute
                  path={`${sites}`}
                  isAuthenticated={isAuth}
                  exact
                  title="Sites"
                  metaContent="Sites"
                >
                  <Sites />
                </PrivateRoute>
                <PrivateRoute
                  path={`${patients}`}
                  isAuthenticated={isAuth}
                  exact
                  title="Patients"
                  metaContent="Patients"
                >
                  <PatientManagement />
                </PrivateRoute>
                <PrivateRoute
                  path={`${patients}/:id${dashboard}`}
                  isAuthenticated={isAuth}
                  exact
                  title="Patients Dashboard Management"
                  metaContent="Patients Dashboard Management"
                >
                  <PatientDashboard />
                </PrivateRoute>
                <PrivateRoute
                  path={[
                    `${patients}/:id${dashboard}/:studyId`,
                    `${patients}/:id${dashboard}/:studyId/:eventId`,
                  ]}
                  isAuthenticated={isAuth}
                  exact
                  title="Disclosure View"
                  metaContent="Disclosure View"
                >
                  <DisclosureView />
                </PrivateRoute>
                <PrivateRoute
                  path={`${patients}${ecgRecord}`}
                  isAuthenticated={isAuth}
                  exact
                  title="Report Management"
                  metaContent="Report Management"
                >
                  <ECGRecordDashboard />
                </PrivateRoute>
                <PrivateRoute
                  path={historyView}
                  isAuthenticated={isAuth}
                  exact
                  title="History"
                  metaContent="History"
                >
                  <History />
                </PrivateRoute>
                <PrivateRoute
                  path={analysisSetting}
                  isAuthenticated={isAuth}
                  exact
                  title="Analysis Setting"
                  metaContent="Analysis Setting"
                >
                  <AnalysisSetting />
                </PrivateRoute>
                <PrivateRoute
                  path={patientTemplate}
                  isAuthenticated={isAuth}
                  exact
                  title="Patient Template"
                  metaContent="Patient Template"
                >
                  <PatientTemplate />
                </PrivateRoute>
                <PrivateRoute
                  path={otherSetting}
                  isAuthenticated={isAuth}
                  exact
                  title="General Setting"
                  metaContent="General Setting"
                >
                  <OtherSetting />
                </PrivateRoute>
                <PrivateRoute
                  path={ssoSetting}
                  isAuthenticated={isAuth}
                  exact
                  title="SSO Setting"
                  metaContent="SSO Setting"
                >
                  <SSOSetting />
                </PrivateRoute>
                <PrivateRoute
                  path={groupManagement}
                  isAuthenticated={isAuth}
                  exact
                  title="Group Management"
                  metaContent="Group Management"
                >
                  <GroupManagement />
                </PrivateRoute>
                <PrivateRoute path={baseUrl} isAuthenticated={isAuth} exact>
                  <SiteEntry />
                </PrivateRoute>
                <PrivateRoute
                  path={authentication}
                  isAuthenticated={isAuth}
                  exact
                  title="Authentication"
                  metaContent="Authentication"
                >
                  <Authentication />
                </PrivateRoute>
                <PrivateRoute
                  path={profile}
                  isAuthenticated={isAuth}
                  exact
                  title="Profile"
                  metaContent="Profile"
                >
                  <Profile />
                </PrivateRoute>
                <PrivateRoute
                  path={apikey}
                  isAuthenticated={isAuth}
                  exact
                  title="APIKey"
                  metaContent="APIKey"
                >
                  <APIKey />
                </PrivateRoute>
                <PrivateRoute
                  path={agentSettings}
                  isAuthenticated={isAuth}
                  exact
                  title="Agent Settings"
                  metaContent="Agent Settings"
                >
                  <AgentSettings />
                </PrivateRoute>
                <PrivateRoute
                  path={oauthConsent}
                  isAuthenticated={isAuth}
                  exact
                  title="Authorize Agent"
                  metaContent="Authorize Agent"
                >
                  <OAuthConsent />
                </PrivateRoute>
                <PrivateRoute
                  path={mcpSetting}
                  isAuthenticated={isAuth}
                  exact
                  title="MCP Settings"
                  metaContent="MCP Settings"
                >
                  <McpSetting />
                </PrivateRoute>
                <PrivateRoute
                  path={userManagement}
                  isAuthenticated={isAuth}
                  exact
                  title="User Management"
                  metaContent="User Management"
                >
                  <UsersManagement />
                </PrivateRoute>
                <PrivateRoute
                  path={`${userManagement}${addPath}`}
                  isAuthenticated={isAuth}
                  exact
                  title="User Management Add"
                  metaContent="User Management Add"
                >
                  <UserAdd />
                </PrivateRoute>
                <PrivateRoute
                  path={`${userManagement}/:id`}
                  isAuthenticated={isAuth}
                  exact
                  title="User Management Edit"
                  metaContent="User Management Edit"
                >
                  <UserEdit />
                </PrivateRoute>
                <PrivateRoute
                  path={clinics}
                  isAuthenticated={isAuth}
                  exact
                  title="Tenant Management"
                  metaContent="Tenant Management"
                >
                  <ClinicsManagement />
                </PrivateRoute>
                <PrivateRoute
                  path={`${clinics}${addPath}`}
                  isAuthenticated={isAuth}
                  exact
                  title="Tenant Add"
                  metaContent="Tenant Add"
                >
                  <ClinicAdd />
                </PrivateRoute>
                <PrivateRoute
                  path={`${clinics}/:id`}
                  isAuthenticated={isAuth}
                  exact
                  title="Tenant Edit"
                  metaContent="Tenant Edit"
                >
                  <ClinicEdit />
                </PrivateRoute>
                <PrivateRoute
                  path={monitoringProtocols}
                  isAuthenticated={isAuth}
                  exact
                  title="Monitoring Protocols Management"
                  metaContent="Monitoring Protocols Management"
                >
                  <MonitoringProtocolsManagement />
                </PrivateRoute>
                <PrivateRoute
                  path={`${monitoringProtocols}${addPath}`}
                  isAuthenticated={isAuth}
                  exact
                  title="Monitoring Protocols Add"
                  metaContent="Monitoring Protocols Add"
                >
                  <MonitoringProtocolsAdd />
                </PrivateRoute>
                <PrivateRoute
                  path={`${monitoringProtocols}/:id`}
                  isAuthenticated={isAuth}
                  exact
                  title="Monitoring Protocols Edit"
                  metaContent="Monitoring Protocols Edit"
                >
                  <MonitoringProtocolsEdit />
                </PrivateRoute>
                <PrivateRoute
                  path={devices}
                  isAuthenticated={isAuth}
                  exact
                  title="Devices Management"
                  metaContent="Devices Management"
                >
                  <DevicesManagement />
                </PrivateRoute>
                <PrivateRoute
                  path={sensors}
                  isAuthenticated={isAuth}
                  exact
                  title="Sensors Management"
                  metaContent="Sensors Management"
                >
                  <SensorsManagement />
                </PrivateRoute>
                <PrivateRoute
                  path={`${patients}${addPath}`}
                  isAuthenticated={isAuth}
                  exact
                  title="Patient Add"
                  metaContent="Patient Add"
                >
                  <PatientsAdd />
                </PrivateRoute>
                <PrivateRoute
                  path={`${patients}/:id`}
                  isAuthenticated={isAuth}
                  exact
                  title="Patient Edit"
                  metaContent="Patient Edit"
                >
                  <PatientsTabs />
                </PrivateRoute>
                <PrivateRoute
                  path={`${patients}/:id${sensors}/:deviceId`}
                  isAuthenticated={isAuth}
                  exact
                  title="Patients Devices Add"
                  metaContent="Patients Devices Add"
                >
                  <PatientSensorAdd />
                </PrivateRoute>
                <PrivateRoute
                  path={dashboardEvent}
                  isAuthenticated={isAuth}
                  exact
                  title="Dashboard Management Events"
                  metaContent="Dashboard Management Events"
                >
                  <DashboardManagementEvent />
                </PrivateRoute>
                <PrivateRoute
                  path={dashboardRpmAlert}
                  isAuthenticated={isAuth}
                  exact
                  title="Dashboard Management RPM Alerts"
                  metaContent="Dashboard Management RPM Alerts"
                >
                  <DashboardManagementRpmAlert />
                </PrivateRoute>
                <PrivateRoute
                  path={process}
                  isAuthenticated={isAuth}
                  exact
                  title="ECG Analysis Process"
                  metaContent="ECG Analysis Process"
                >
                  <ProcessManagement />
                </PrivateRoute>
                <PrivateRoute
                  path={reports}
                  isAuthenticated={isAuth}
                  exact
                  title="Report Management"
                  metaContent="Report Management"
                >
                  <ReportsManagement />
                </PrivateRoute>
                <PrivateRoute
                  path={reportArchive}
                  isAuthenticated={isAuth}
                  exact
                  title="Archive Management"
                  metaContent="Archive Management"
                >
                  <ArchiveManagement />
                </PrivateRoute>
                <PrivateRoute
                  path={`${reports}${generateReport}`}
                  isAuthenticated={isAuth}
                  exact
                  title="Generate Report"
                  metaContent="Generate Report"
                >
                  <GenerateReport />
                </PrivateRoute>
                <PrivateRoute
                  path={ecgDataChunks}
                  isAuthenticated={isAuth}
                  exact
                  title="ECG Data Chunks"
                  metaContent="ECG Data Chunks"
                >
                  <EcgDataChunks />
                </PrivateRoute>
                <Route path={`${download}`} exact>
                  <Helmet>
                    <title>Download</title>
                    <meta name="description" content="Download" />
                  </Helmet>
                  <Download />
                </Route>
                <Route path={forbidden} exact>
                  <Helmet>
                    <title>403</title>
                    <meta name="description" content="Not Enough Permissions" />
                  </Helmet>
                  <NotFound code={403} message="Not Enough Permissions" />
                </Route>
                <Route path={restorePassword} exact>
                  <Helmet>
                    <title>Restore Password</title>
                    <meta name="description" content="Restore Password" />
                  </Helmet>
                  <RestorePassword />
                </Route>
                <Route>
                  <Helmet>
                    <title>404</title>
                    <meta name="description" content="Page Not Found" />
                  </Helmet>
                  <NotFound code={404} message="Page Not Found" />
                </Route>
              </Switch>
            </Layout>
          </Switch>
        </Suspense>
      </BrowserRouter>
    </Loader>
  );
}
